"use client"

import * as ResizablePrimitive from "react-resizable-panels"
import type * as React from "react"

import { cn } from "../../lib/utils"

type ResizablePanelGroupProps = React.HTMLAttributes<HTMLElement> & {
  autoSaveId?: string
  direction?: "horizontal" | "vertical"
  orientation?: "horizontal" | "vertical"
  id?: string
  keyboardResizeBy?: number | null
  onLayout?: (layout: number[]) => void
  onLayoutChange?: (layout: Record<string, number>) => void
  onLayoutChanged?: (layout: Record<string, number>) => void
  storage?: unknown
}

type ResizableHandlePrimitiveProps = React.HTMLAttributes<HTMLElement> & {
  disabled?: boolean
  hitAreaMargins?: {
    coarse: number
    fine: number
  }
  id?: string
  onDragging?: (isDragging: boolean) => void
}

type AnyResizableComponent = React.ComponentType<
  React.PropsWithChildren<Record<string, unknown>>
>

type ResizablePanelsCompat = {
  Group?: AnyResizableComponent
  PanelGroup?: AnyResizableComponent
  PanelResizeHandle?: AnyResizableComponent
  Separator?: AnyResizableComponent
}

const resizableCompat = ResizablePrimitive as unknown as ResizablePanelsCompat
const usesPanelGroupApi = resizableCompat.PanelGroup != null

function requiredComponent(
  component: AnyResizableComponent | undefined,
  name: string
): AnyResizableComponent {
  if (!component) {
    throw new Error(`react-resizable-panels export missing: ${name}`)
  }
  return component
}

const PrimitivePanelGroup = requiredComponent(
  resizableCompat.PanelGroup ?? resizableCompat.Group,
  "PanelGroup/Group"
)
const PrimitiveResizeHandle = requiredComponent(
  resizableCompat.PanelResizeHandle ?? resizableCompat.Separator,
  "PanelResizeHandle/Separator"
)

type PanelGroupLayout = number[] | Record<string, number>

function toLayoutArray(layout: PanelGroupLayout): number[] {
  return Array.isArray(layout) ? layout : Object.values(layout)
}

function toLayoutRecord(layout: PanelGroupLayout): Record<string, number> {
  if (!Array.isArray(layout)) {
    return layout
  }

  return layout.reduce<Record<string, number>>((acc, size, index) => {
    acc[String(index)] = size
    return acc
  }, {})
}

function ResizablePanelGroup({
  className,
  direction,
  orientation,
  onLayout,
  onLayoutChange,
  onLayoutChanged,
  ...props
}: ResizablePanelGroupProps) {
  const resolvedOrientation = direction ?? orientation ?? "horizontal"
  const handleLayout =
    onLayout || onLayoutChange
      ? (layout: PanelGroupLayout) => {
          onLayout?.(toLayoutArray(layout))
          onLayoutChange?.(toLayoutRecord(layout))
        }
      : undefined
  const handleLayoutChanged = onLayoutChanged
    ? (layout: PanelGroupLayout) => {
        onLayoutChanged(toLayoutRecord(layout))
      }
    : undefined
  const primitiveProps = usesPanelGroupApi
    ? {
        direction: resolvedOrientation,
        onLayout: handleLayout,
      }
    : {
        orientation: resolvedOrientation,
        onLayoutChange: handleLayout,
        onLayoutChanged: handleLayoutChanged,
      }

  return (
    <PrimitivePanelGroup
      {...primitiveProps}
      data-slot="resizable-panel-group"
      className={cn(
        "cn-resizable-panel-group flex h-full w-full aria-[orientation=vertical]:flex-col",
        className
      )}
      {...props}
    />
  )
}

function ResizablePanel({
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.Panel>) {
  return <ResizablePrimitive.Panel data-slot="resizable-panel" {...props} />
}

function ResizableHandle({
  withHandle,
  className,
  ...props
}: ResizableHandlePrimitiveProps & {
  withHandle?: boolean
}) {
  return (
    <PrimitiveResizeHandle
      data-slot="resizable-handle"
      className={cn(
        "cn-resizable-handle relative flex w-px items-center justify-center bg-border ring-offset-background after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:translate-x-0 aria-[orientation=horizontal]:after:-translate-y-1/2 [&[aria-orientation=horizontal]>div]:rotate-90",
        className
      )}
      {...props}
    >
      {withHandle && (
        <div className="cn-resizable-handle-icon z-10 flex shrink-0" />
      )}
    </PrimitiveResizeHandle>
  )
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup }
